import { forwardRef } from 'react'
import { Turnstile } from '@marsidev/react-turnstile'
import type { TurnstileInstance } from '@marsidev/react-turnstile'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { getEnv } from 'utils/env'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import useStore from 'stores/utils/hooks/useStore'

interface CaptchaWidgetProps {
  onSuccess?: (token: string) => void
  onError?: (error: string) => void
  onWidgetLoad?: (widgetID: string) => void
}

const CaptchaWidget = forwardRef<
  TurnstileInstance | undefined,
  CaptchaWidgetProps
>(({ onSuccess, onError, onWidgetLoad }, ref) => {
  const {
    navigation: { currentPageInSession },
    captcha: { setCaptchaFlowStatus }
  } = useStore()

  const handleError = (error: string) => {
    trackInAmplitude(AmplitudeEvent.CaptchaError, {
      error,
      currentPage: currentPageInSession,
      timestamp: new Date().toISOString()
    })
    setCaptchaFlowStatus('error')

    if (onError) {
      onError(error)
    }
  }

  const handleWidgetLoad = (widgetID: string) => {
    trackInAmplitude(AmplitudeEvent.CaptchaWidgetLoaded, {
      currentPage: currentPageInSession,
      widgetID
    })

    if (onWidgetLoad) {
      onWidgetLoad(widgetID)
    }
  }

  const handleUnsupported = () => {
    trackInAmplitude(AmplitudeEvent.CaptchaUnsupported, {
      currentPage: currentPageInSession
    })

    setCaptchaFlowStatus('error')
  }

  const handleSuccess = (token: string) => {
    trackInAmplitude(AmplitudeEvent.CaptchaSuccess, {
      currentPage: currentPageInSession
    })

    if (onSuccess) {
      onSuccess(token)
    }
  }

  const handleExpire = () => {
    trackInAmplitude(AmplitudeEvent.CaptchaExpired, {
      currentPage: currentPageInSession
    })

    setCaptchaFlowStatus('error')
  }

  const handleTimeout = () => {
    trackInAmplitude(AmplitudeEvent.CaptchaTimeout, {
      currentPage: currentPageInSession
    })

    setCaptchaFlowStatus('error')
  }

  const handleBeforeInteractive = () => {
    trackInAmplitude(AmplitudeEvent.CaptchaBeforeInteractive, {
      currentPage: currentPageInSession
    })
  }

  const handleAfterInteractive = () => {
    trackInAmplitude(AmplitudeEvent.CaptchaAfterInteractive, {
      currentPage: currentPageInSession
    })
  }

  const SITE_KEY = getEnv('REACT_APP_TURNSTILE_SITE_KEY') || ''

  if (!SITE_KEY) {
    logAndTrackError(new Error('Turnstile site key is not defined'))
    return null
  }

  return (
    <Turnstile
      ref={ref}
      siteKey={SITE_KEY}
      options={{
        execution: 'execute'
      }}
      onSuccess={handleSuccess}
      onError={handleError}
      onWidgetLoad={handleWidgetLoad}
      onUnsupported={handleUnsupported}
      onExpire={handleExpire}
      onTimeout={handleTimeout}
      onBeforeInteractive={handleBeforeInteractive}
      onAfterInteractive={handleAfterInteractive}
    />
  )
})

CaptchaWidget.displayName = 'CaptchaWidget'

export { CaptchaWidget }
